import React from 'react';
import {
    Alert,
    Badge,
    Button,
    Flex,
    Loader,
    Paper,
    Stack,
    Text,
} from '@mantine/core';
import {
    IconAlertTriangle,
    IconPencil,
    IconTrash,
    IconUser,
    IconUserStar,
} from '@tabler/icons-react';
import { When } from '../common';
import { SurveyZoneStoreCount } from '../survey/SurveyZoneStoreCount';
import { formatUserLabel } from './helpers';
import { VirtualDivisionRow, VirtualHierarchyUser } from './types';

type Props = {
    division: VirtualDivisionRow;
    deleting: boolean;
    onEdit: (division: VirtualDivisionRow) => void;
    onDelete: (division: VirtualDivisionRow) => void;
};

function sapSourceLabel(division: VirtualDivisionRow): string {
    const sourceDivision = division.sourceDivisionName.trim();
    const sourceOpsArea = division.sourceOpsAreaName?.trim();
    if (sourceDivision && sourceOpsArea) {
        return `${sourceDivision} · ${sourceOpsArea}`;
    }
    return sourceDivision || sourceOpsArea || 'No SAP source yet';
}

function GroupingBadge({ division }: { division: VirtualDivisionRow }) {
    if (!division.virtualOpsArea) {
        return (
            <Badge
                size="sm"
                variant="light"
                color="blue"
                radius="xl"
                tt="none"
                className="w-fit"
            >
                In SAP ops area
            </Badge>
        );
    }

    return (
        <Badge
            size="sm"
            variant="light"
            color="teal"
            radius="xl"
            tt="none"
            className="w-fit"
        >
            Virtual ops area
        </Badge>
    );
}

function RoleField({
    label,
    user,
    icon,
    emptyLabel,
}: {
    label: string;
    user?: VirtualHierarchyUser | null;
    icon: React.ReactNode;
    emptyLabel: string;
}) {
    return (
        <Stack gap={2} className="min-w-0 flex-1">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {label}
            </Text>
            <Flex align="center" gap={6} className="min-w-0">
                <span className="flex-shrink-0 text-gray-400">{icon}</span>
                <Text size="sm" className="min-w-0 truncate">
                    {user ? formatUserLabel(user) : emptyLabel}
                </Text>
            </Flex>
        </Stack>
    );
}

function OverlayActions({
    division,
    onEdit,
    onDelete,
}: Pick<Props, 'division' | 'onEdit' | 'onDelete'>) {
    return (
        <Flex gap={4} align="center">
            <Button
                type="button"
                variant="subtle"
                size="xs"
                radius="md"
                fullWidth={false}
                aria-label="Edit virtual division"
                className="text-primary p-1 w-min h-min"
                onClick={() => onEdit(division)}
            >
                <IconPencil size={16} />
            </Button>
            <Button
                type="button"
                variant="subtle"
                color="red"
                size="xs"
                radius="md"
                fullWidth={false}
                aria-label="Delete virtual division"
                className="p-1 w-min h-min"
                onClick={() => onDelete(division)}
            >
                <IconTrash size={16} />
            </Button>
        </Flex>
    );
}

export function VirtualHierarchyOverlayCard({
    division,
    deleting,
    onEdit,
    onDelete,
}: Props) {
    const cardClassName = deleting ? 'opacity-50 pointer-events-none' : '';
    const displayableStores = division.stores.filter(
        store => !!store?.storeNo?.trim() || !!store?.name?.trim(),
    );
    const groupingParentLabel = division.virtualOpsArea
        ? division.virtualOpsArea.name
        : division.sapOpsAreaName;

    return (
        <Paper withBorder radius="lg" p="md" className={cardClassName}>
            <Flex justify="space-between" align="flex-start" gap="sm">
                <Stack gap={4} className="min-w-0">
                    <Flex align="center" gap="xs" wrap="wrap">
                        <Text size="sm" fw={600}>
                            {division.name}
                        </Text>
                        <GroupingBadge division={division} />
                        <When condition={displayableStores.length === 0}>
                            <Badge
                                size="sm"
                                variant="light"
                                color="orange"
                                radius="xl"
                                tt="none"
                                leftSection={<IconAlertTriangle size={12} />}
                                className="w-fit"
                            >
                                No stores
                            </Badge>
                        </When>
                    </Flex>
                    {groupingParentLabel ? (
                        <Text size="xs" c="dimmed">
                            {groupingParentLabel}
                        </Text>
                    ) : null}
                </Stack>
                {deleting ? (
                    <Loader size="xs" />
                ) : (
                    <OverlayActions
                        division={division}
                        onEdit={onEdit}
                        onDelete={onDelete}
                    />
                )}
            </Flex>
            <Flex gap="lg" align="flex-start" wrap="wrap" className="mt-3">
                <RoleField
                    label="Virtual divisional manager"
                    user={division.manager}
                    icon={<IconUser size={16} />}
                    emptyLabel="Not assigned"
                />
                {division.virtualOpsArea ? (
                    <RoleField
                        label="Virtual ops executive"
                        user={division.virtualOpsArea.operationsExecutive}
                        icon={<IconUserStar size={16} />}
                        emptyLabel="Not assigned"
                    />
                ) : null}
            </Flex>
            <Flex
                gap="lg"
                justify="space-between"
                align="flex-start"
                className="mt-3"
            >
                <Stack gap={2} className="min-w-0 flex-1">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        SAP source
                    </Text>
                    <Text size="sm">{sapSourceLabel(division)}</Text>
                </Stack>
                <Stack gap={2} className="min-w-0 flex-1">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Stores
                    </Text>
                    <When
                        condition={displayableStores.length === 0}
                        fallback={
                            <SurveyZoneStoreCount stores={displayableStores} />
                        }
                    >
                        <Alert
                            color="orange"
                            variant="light"
                            icon={<IconAlertTriangle size={16} />}
                        >
                            This virtual division has no active stores. Edit the
                            division to add stores.
                        </Alert>
                    </When>
                </Stack>
            </Flex>
        </Paper>
    );
}

export function VirtualHierarchyEmptyState() {
    return (
        <Paper
            withBorder
            radius="lg"
            p="lg"
            className="border-dashed border-gray-300 bg-gray-50"
        >
            <Stack align="center" gap="sm" className="text-center">
                <Text size="sm" fw={500}>
                    No virtual overlays yet
                </Text>
                <Text size="sm" c="dimmed" className="max-w-lg">
                    A virtual division is a named store subset with its own
                    virtual divisional manager. A virtual ops area is optional.
                    Stores keep their real SAP division.
                </Text>
            </Stack>
        </Paper>
    );
}
